/**
 * Builds the fetchLocations payload for a rectangular-area search.
 *
 * Shared by searchWithinMap.plugin (explicit user click / map moves) and
 * autoSearchWithinMap.plugin (automatic activation when the viewport drifts
 * away from the backend-suggested bounds) so both flows request the same
 * shape of rectangular-area search from the API.
 *
 * Pure — no side effects, no SDK coupling.
 *
 * @module searchResultsMap/services/rectangularAreaSearchPayload
 */

import { LocationType } from 'features/searchResultsFacets';
import { getViewportPaddingForSitePlans, expandBounds } from './sitePlanViewportPadding';
import { areBoundsValid } from './geometry';

const RECTANGULAR_AREA = LocationType.RectangularArea.toString();

/**
 * @param {{ south: number, north: number, west: number, east: number }|null} bounds
 * @param {number} zoom
 * @returns {Object|null} fetchLocations payload, or null when bounds are missing/invalid
 */
function createFetchLocationsPayload(bounds, zoom) {
  if (!bounds) {
    return null;
  }

  // Expand bounds slightly when site plans are active so partially-visible site plan
  // polygons near the viewport edges are not prematurely dropped from results.
  const padding = getViewportPaddingForSitePlans(zoom ?? 0);
  const paddedBounds = padding > 0 ? expandBounds(bounds, padding) : bounds;

  if (!areBoundsValid(paddedBounds)) {
    return null;
  }

  const { south, north, west, east } = paddedBounds;

  return {
    locationType: RECTANGULAR_AREA,
    minimumLatitude: south,
    maximumLatitude: north,
    minimumLongitude: west,
    maximumLongitude: east,
  };
}

export default createFetchLocationsPayload;